import React, { useCallback, useEffect, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import isEmpty from 'lodash/isEmpty';

import { BuilderComponent, getFontStyle, useNode, OpeningHour } from '@mnz/builder';
import StyledComponent from '../../core/StyledComponent';
import { OpeningHoursSetting, OpeningHoursSettingProps } from './OpeningHoursSetting';
import { getDurationString, getDayOfWeek } from '../../core/date';
import { AccordionSummary } from '../../common/components/mui/Accordion';
import { getBorderCSS, hoverAnimationCss } from '../../common/style';
import { px2rem } from '../../core';
import { utcToZonedTime } from 'date-fns-tz';
import { useTheme, alpha } from '@mui/material';
import { useInterval } from 'react-use';

export const OpeningHours: BuilderComponent<OpeningHoursSettingProps> = ({
    account,
    designSettings,
    hideDay,
    style,
    ...props
}) => {
    const theme = useTheme();
    const [isOpen, setIsOpen] = useState(false);
    const {
        connectors: { connect, drag },
        hidden,
        query: { getOptions },
    } = useNode((node) => ({
        hidden: node.data.hidden,
    }));

    const isEmptyData = isEmpty(account.openingHours);
    const canEdit = getOptions().enabled;

    const renderTimeRanges = (openingHours: OpeningHour[], dayOfWeek: number) => {
        if (hideDay[dayOfWeek]) {
            return <StyledComponent>Closed</StyledComponent>;
        }
        let time = openingHours.find((i) => {
            if (i.dayOfWeek === dayOfWeek) {
                return i;
            }
        });
        if (dayOfWeek === 7 || dayOfWeek === 0) {
            time = openingHours.find((i) => i.dayOfWeek === 0);
        }
        if (time?.allDay) {
            return <StyledComponent>24 hours</StyledComponent>;
        }
        if (!time || time.times.length === 0) {
            return <StyledComponent>Closed</StyledComponent>;
        }
        return time.times.map((t, i) => (
            <StyledComponent key={i}>
                {getDurationString(t.start)} to {getDurationString(t.end)}
                {i !== time.times.length - 1 && <span>, </span>}
            </StyledComponent>
        ));
    };

    const checkIsOpen = useCallback(() => {
        let result = false;
        let currentTime = new Date();
        if (account.timezone) {
            currentTime = utcToZonedTime(currentTime, account.timezone);
        }
        const day = currentTime.getDay();
        const timeNumber = currentTime.getHours() * 60 + currentTime.getMinutes();
        const openingHour = account.openingHours.find((hour) => hour.dayOfWeek === day);
        if (openingHour && !hideDay[openingHour.dayOfWeek]) {
            if (openingHour.allDay) return true;
            for (let i = 0; i < openingHour.times.length; i++) {
                if (timeNumber > openingHour.times[i].start && timeNumber < openingHour.times[i].end) {
                    result = true;
                    return result;
                }
            }
        }
        return result;
    }, [account.openingHours]);

    useEffect(() => {
        setIsOpen(checkIsOpen());
    }, []);
    useInterval(() => {
        setIsOpen(checkIsOpen());
    }, 1000);

    return (
        <StyledComponent>
            {!hidden && !isEmptyData && (
                <>
                    <StyledComponent
                        xs={{
                            padding: `${px2rem(7)} ${px2rem(10)}}`,
                            display: 'flex',
                            alignItems: 'center',
                            top: 20,
                            left: 20,
                            position: 'absolute',
                            color: isOpen ? theme.palette.success.main : theme.palette.error.main,
                            borderRadius: `${px2rem(5)}`,
                            background: alpha(theme.palette.primary.main, 0.6),
                            fontFamily: theme.typography.fontFamily,
                            zIndex: 10,
                        }}
                    >
                        <StyledComponent
                            xs={{
                                width: px2rem(8),
                                height: px2rem(8),
                                marginRight: px2rem(4),
                                borderRadius: px2rem(4),
                                background: isOpen ? theme.palette.success.main : theme.palette.error.main,
                            }}
                        />
                        <StyledComponent xs={{ fontSize: px2rem(12), lineHeight: px2rem(12), fontWeight: 600 }}>
                            {isOpen ? 'Open' : 'Closed'}
                        </StyledComponent>
                    </StyledComponent>
                    <Box
                        sx={{
                            marginX: '35px',
                            marginTop: '16px',
                            ...hoverAnimationCss({ content: '"Opening Hours"', active: canEdit }),
                        }}
                    >
                        <Accordion
                            square
                            sx={{
                                color: designSettings.colors.buttonText.color,
                                backgroundColor: 'inherit',
                                ...getFontStyle(designSettings, 'secondary'),
                                ...getBorderCSS({
                                    corner: designSettings.styles.corner,
                                    color: designSettings.colors.button.color,
                                    buttonStyle: designSettings.styles.button,
                                    maxBorderRadius: 1.5,
                                }),
                            }}
                        >
                            <AccordionSummary sx={{ paddingLeft: '40px', paddingRight: '20px' }}>
                                <Typography
                                    sx={{
                                        ...getFontStyle(designSettings, 'secondary', 'bold'),
                                        width: '100%',
                                        textAlign: 'center',
                                    }}
                                >
                                    Opening Hours
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <StyledComponent ref={(ref) => connect(drag(ref))}>
                                    {Array.from(Array(7).keys()).map((i) => (
                                        <StyledComponent
                                            key={i}
                                            xs={{
                                                fontSize: px2rem(14),
                                                display: 'flex',
                                                flexDirection: 'row',
                                                paddingTop: px2rem(10),
                                                paddingBottom: px2rem(10),
                                                '&:last-child': {
                                                    border: 'none',
                                                },
                                                ...((style && style.xs) || {}),
                                            }}
                                            sm={{
                                                fontSize: px2rem(16),
                                                paddingTop: px2rem(16),
                                                flexDirection: 'row',
                                                ...((style && style.sm) || {}),
                                            }}
                                            {...props}
                                        >
                                            <StyledComponent
                                                xs={{
                                                    flexGrow: 1,
                                                    flexShrink: 0,
                                                    paddingRight: px2rem(8),
                                                    ...getFontStyle(designSettings, 'secondary', 'regular'),
                                                    ...((style && style.dayOfWeek.xs) || {}),
                                                }}
                                            >
                                                {getDayOfWeek(i)}
                                            </StyledComponent>
                                            <StyledComponent
                                                xs={{ textAlign: 'right', ...((style && style.timeRange.xs) || {}) }}
                                                sm={{ textAlign: 'right', ...((style && style.timeRange.sm) || {}) }}
                                            >
                                                {renderTimeRanges(account.openingHours, i + 1)}
                                            </StyledComponent>
                                        </StyledComponent>
                                    ))}
                                </StyledComponent>
                            </AccordionDetails>
                        </Accordion>
                    </Box>
                </>
            )}
        </StyledComponent>
    );
};

OpeningHours.defaultProps = {
    hideDay: {},
};

OpeningHours.settings = {
    displayName: 'Opening Hours',
    related: {
        settings: OpeningHoursSetting,
    },
    rules: {
        canDrag: () => false,
    },
    custom: {
        showBox: false,
    },
};
